import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { chooseAlerts, useAlerts } from '../alerts';
import { appVersion, buildDate, fullDate } from '../build';
import { askToNotify, onGranted, supported } from '../notify';
import {
  SOUNDS, SoundRefused, adoptSound, choiceHint, chooseNone, chooseStandard, hush, play,
  useSoundChoice, type SoundEvent,
} from '../sounds';
import { chooseTheme, useThemeMode, type ThemeMode } from '../theme';
import { reloadNow, useNewBuild } from '../update';
import { Icon, type IconName } from '../material';
import { Button, Chevron, ListRow, Page, Sheet, Switcher, useErrorHaptic } from '../widgets';

/**
 * «Настройки» — a port of `settings_screen.dart`, less what a browser has no
 * equivalent of: the phone's «Картинка под строкой состояния» is about a status bar
 * a page does not draw under, and «Не усыплять приложение» about a battery setting a
 * page cannot reach.
 *
 * «Тема» is chosen in the row itself: three answers, one word each, and seeing the
 * app turn over under the thumb is the whole of what the setting does. «Приложение»
 * is a card of its own, last: not a setting but the way to the app itself.
 *
 * «Звуки» is the three moments of a ride the app sounds, and what each sounds like
 * (ADR 0031) — a card of its own, under the one that decides whether anything is
 * said at all.
 */
export function SettingsScreen() {
  const navigate = useNavigate();
  return (
    <Page title="Настройки" fallback="/profile">
      <div className="settings">
        <div className="list-card">
          <ThemeRow />
        </div>
        {supported() && <AlertsCard />}
        <SoundsCard />
        <div className="list-card">
          <ListRow
            title="Приложение"
            hint={`Версия ${appVersion}`}
            trailing={<Chevron />}
            onClick={() => navigate('/settings/app')}
          />
        </div>
        <p className="settings-foot">Лимузин · Web {appVersion}</p>
      </div>
    </Page>
  );
}

const THEMES: { mode: ThemeMode; icon: IconName; label: string }[] = [
  { mode: 'system', icon: 'smartphone', label: 'Системная' },
  { mode: 'light', icon: 'light_mode_outlined', label: 'Светлая' },
  { mode: 'dark', icon: 'dark_mode_outlined', label: 'Тёмная' },
];

function ThemeRow() {
  const mode = useThemeMode();
  const current = THEMES.find((option) => option.mode === mode)!;
  return (
    <ListRow
      title="Тема"
      hint={current.label}
      trailing={
        <span className="theme-segments" role="radiogroup" aria-label="Тема">
          {THEMES.map((option) => (
            <button
              key={option.mode}
              type="button"
              role="radio"
              aria-checked={option.mode === mode}
              aria-label={option.label}
              title={option.label}
              className={option.mode === mode ? 'on' : ''}
              onClick={() => chooseTheme(option.mode)}
            >
              <Icon name={option.icon} size={17} />
            </button>
          ))}
        </span>
      }
    />
  );
}

/** What the browser has said about notifications, heard again when it says more. */
function usePermission(): NotificationPermission {
  return useSyncExternalStore(
    (heard) => onGranted(heard),
    () => Notification.permission,
  );
}

/**
 * «Уведомления о поездке». On the phone the switch holds the socket open while the
 * app is out of sight; here it is whether this browser is told anything with no tab
 * in front of the passenger (ADR 0027). The browser keeps its own permission beside
 * it, which a page can ask for but never take back — so where it has been refused,
 * the row says where to change it.
 */
function AlertsCard() {
  const on = useAlerts();
  const permission = usePermission();
  const refused = permission === 'denied';

  const flip = (next: boolean) => {
    chooseAlerts(next);
    // Asked from the tap itself: a browser shows the question only to somebody who
    // is doing something.
    if (next) askToNotify();
  };

  return (
    <div className="list-card">
      <ListRow
        title="Уведомления о поездке"
        hint={
          refused && on
            ? 'Запрещены в настройках браузера — разрешите их для этого сайта'
            : 'Сообщим, когда водитель выедет по предзаказу'
        }
        trailing={<Switcher on={on} onChange={flip} label="Уведомления о поездке" />}
        onClick={() => flip(!on)}
      />
    </div>
  );
}

/**
 * «Звуки»: a row for each moment of a ride that is heard, saying what it sounds like
 * now and opening the sheet that changes it. No heading over them, like the rest of
 * this screen — the hint says «Звук» on every row instead, which is what tells a
 * passenger that «Заказ принят» here is a thing to be heard and not a state of their
 * order.
 */
function SoundsCard() {
  const [open, setOpen] = useState<SoundEvent | null>(null);
  return (
    <>
      <div className="list-card">
        {SOUNDS.map((event) => (
          <SoundRow key={event.key} event={event} onClick={() => setOpen(event)} />
        ))}
      </div>
      {open && <SoundSheet event={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function SoundRow({ event, onClick }: { event: SoundEvent; onClick: () => void }) {
  const choice = useSoundChoice(event);
  return (
    <ListRow
      title={event.title}
      hint={choiceHint(choice)}
      trailing={<Chevron />}
      onClick={onClick}
    />
  );
}

/**
 * What one moment sounds like: the chime the app came with, nothing, or a file off
 * the device. A port of `sound_sheet.dart`.
 *
 * A tap on a sound chooses it and plays it, because the only way to choose between
 * sounds is to hear them. Nothing here waits on «Сохранить»: the choice is this
 * browser's own and is made as it is tapped, like «Тема».
 */
function SoundSheet({ event, onClose }: { event: SoundEvent; onClose: () => void }) {
  const choice = useSoundChoice(event);
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  useErrorHaptic(problem);

  // A sound being tried does not outlive the sheet it was tried in.
  useEffect(() => hush, []);

  const picked = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setProblem(null);
    hush();
    try {
      await adoptSound(event, file);
      play(event);
    } catch (refusal) {
      setProblem(
        refusal instanceof SoundRefused ? refusal.message : 'Не получилось открыть выбор файла.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      title={event.title}
      hint="Звук приложения, тишина или свой файл с устройства."
      onClose={onClose}
    >
      <div className="list-card" role="radiogroup" aria-label={event.title}>
        <SoundOption
          label="Стандартный"
          chosen={choice.kind === 'standard'}
          audible
          onClick={() => {
            setProblem(null);
            chooseStandard(event);
            play(event);
          }}
        />
        <SoundOption
          label="Без звука"
          chosen={choice.kind === 'none'}
          onClick={() => {
            setProblem(null);
            hush();
            chooseNone(event);
          }}
        />
        {/* The file already chosen: a tap plays it again. */}
        {choice.kind === 'own' && (
          <SoundOption label={choice.name} chosen audible onClick={() => play(event)} />
        )}
      </div>
      {problem && <p className="error-note">{problem}</p>}
      <Button
        tone="plain"
        size="card"
        icon="attach_file_rounded"
        busy={busy}
        style={{ marginTop: 14 }}
        onClick={() => input.current?.click()}
      >
        {choice.kind === 'own' ? 'Выбрать другой файл…' : 'Свой файл…'}
      </Button>
      <input
        ref={input}
        type="file"
        accept="audio/*"
        hidden
        onChange={(change) => {
          const file = change.target.files?.[0];
          // Emptied, so that picking the same file again is still a change.
          change.target.value = '';
          void picked(file);
        }}
      />
    </Sheet>
  );
}

function SoundOption({
  label,
  chosen,
  audible = false,
  onClick,
}: {
  label: string;
  chosen: boolean;
  /** A tap on this one is heard, and the row says so. */
  audible?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={chosen}
      className={chosen ? 'list-row tappable sound-option on' : 'list-row tappable sound-option'}
      onClick={onClick}
    >
      <Icon name={chosen ? 'check_circle' : 'radio_button_unchecked'} size={20} />
      <span className="grow list-row-title">{label}</span>
      {audible && <Icon name="play_arrow_rounded" size={20} style={{ color: 'var(--muted)' }} />}
    </button>
  );
}

/**
 * «Приложение» — which build of it this is. A port of `about_screen.dart`, whose
 * update block is smaller here: the server has the new build already, and «Обновить»
 * is a reload — offered once the page has learned of one (`update.ts`). At the foot, the credit the facts on «Ищем водителя» owe
 * Wikipedia: they are CC BY-SA, and the search screen itself shows them without one.
 */
export function AboutScreen() {
  const { waiting } = useNewBuild();
  return (
    <Page title="Приложение" fallback="/settings">
      <div className="settings" style={{ paddingTop: 26 }}>
        <AppMark />
        <div className="list-card" style={{ marginTop: 24 }}>
          <ListRow title="Версия" trailing={<span className="fact">{appVersion}</span>} />
          <ListRow
            title="Дата сборки"
            // A build with nothing baked in says so: a plausible wrong date is worse
            // than an honest blank.
            trailing={<span className="fact">{buildDate ? fullDate(buildDate) : 'Неизвестна'}</span>}
          />
        </div>
        <div className="panel" style={{ marginTop: 22 }}>
          <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
            <Icon name="refresh" size={20} style={{ color: 'var(--accent)', marginTop: 1 }} />
            <p className="body" style={{ margin: 0, fontSize: 14.5 }}>
              {waiting
                ? 'Вышла новая версия приложения.'
                : 'Веб-версия обновляется сама. Когда выйдет новая, приложение загрузит её или предложит обновиться.'}
            </p>
          </div>
          {waiting && (
            <div style={{ marginTop: 14 }}>
              <Button onClick={reloadNow}>Обновить</Button>
            </div>
          )}
        </div>
        <p className="caption" style={{ margin: '28px 0 0', textAlign: 'center', fontSize: 12.5 }}>
          Факты на экране поиска — из рубрики «Знаете ли вы?» Википедии, CC BY-SA
        </p>
      </div>
    </Page>
  );
}

/** The mark, the name, and what this app is for the person holding it. */
function AppMark() {
  return (
    <div style={{ textAlign: 'center' }}>
      <img className="app-mark" src="/app-mark.png" alt="" width={76} height={76} />
      <h2 className="title" style={{ fontSize: 24, marginTop: 16 }}>
        Лимузин
      </h2>
      <p className="body" style={{ margin: '6px 0 0' }}>
        Приложение пассажира
      </p>
    </div>
  );
}
